import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '@/store';
import { Send, X, Loader2, CheckCircle2, Ban } from 'lucide-react';

export default function BulkBillingIndicator() {
  const [job, setJob] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [open, setOpen] = useState(false);
  const pollRef = useRef(null);

  const fetchJob = useCallback(async () => {
    let j = null;
    try {
      const r = await api.get('/customers/bulk-billing/current');
      j = r.data.job;
      if (j && !j.dismissed) setJob(j); else setJob(null);
    } catch { setJob(null); }
    // campanha de mensagem em massa ativa
    try {
      const rc = await api.get('/customers/campaign/active');
      setCampaign(rc.data.active ? rc.data : null);
    } catch { setCampaign(null); }
    return j || (campaign ? {status:'running'} : null);
  }, [campaign]);

  const stopCampaign = async () => {
    if (!campaign) return;
    try { await api.post(`/customers/campaign/${campaign.campaign_id}/stop`); fetchJob(); } catch {}
  };
  const stopAllCampaigns = async () => {
    try { await api.post('/customers/campaign/stop-all'); fetchJob(); } catch {}
  };

  // poll enquanto houver job (mais rápido se estiver rodando)
  useEffect(() => {
    fetchJob();
    const tick = async () => {
      const j = await fetchJob();
      const interval = j && j.status === 'running' ? 1500 : 8000;
      pollRef.current = setTimeout(tick, interval);
    };
    pollRef.current = setTimeout(tick, 2000);
    const onStart = () => { fetchJob(); setOpen(true); };
    window.addEventListener('bulk-billing-started', onStart);
    return () => { clearTimeout(pollRef.current); window.removeEventListener('bulk-billing-started', onStart); };
  }, [fetchJob]);

  const cancel = async () => {
    if (!job) return;
    try { if (job?.id) await api.post(`/customers/bulk-billing/${job.id}/cancel`); fetchJob(); }
    catch { /* ignore */ }
  };
  const dismiss = async () => {
    try { await api.post('/customers/bulk-billing/dismiss'); } catch { /* ignore */ }
    setJob(null); setOpen(false);
  };

  if (!job && !campaign) return null;

  const total = job ? (job.total || 0) : (campaign?.total || 0);
  const done = job ? (job.processed || 0) : (campaign?.sent || 0);
  const pct = total ? Math.round((done / total) * 100) : 0;
  const running = job ? job.status === 'running' : (campaign?.status === 'running' || campaign?.status === 'sending');
  const isCampaign = !job && !!campaign;
  const circumference = 2 * Math.PI * 20; // r=20

  const statusColor = running ? '#22c55e' : (job?.status === 'cancelled' ? '#f59e0b' : '#22c55e');

  return (
    <>
      {/* Bolinha */}
      <button
        onClick={() => setOpen((v) => !v)}
        className={`fixed bottom-24 right-5 z-[9998] w-14 h-14 rounded-full bg-[#0e1524] border border-border shadow-2xl flex items-center justify-center hover:scale-105 transition-transform ${running ? "bulk-breathe" : ""}`}
        title="Envio de cobrança em massa"
      >
        <svg className="absolute inset-0 -rotate-90" width="56" height="56" viewBox="0 0 48 48">
          <circle cx="24" cy="24" r="20" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="4" />
          <circle cx="24" cy="24" r="20" fill="none" stroke={statusColor} strokeWidth="4" strokeLinecap="round"
            strokeDasharray={circumference} strokeDashoffset={circumference * (1 - pct / 100)}
            style={{ transition: 'stroke-dashoffset .5s ease' }} />
        </svg>
        {running ? <Send className="w-5 h-5 text-primary" />
          : job?.status === 'cancelled' ? <Ban className="w-5 h-5 text-amber-500" />
          : <CheckCircle2 className="w-5 h-5 text-green-500" />}
        <span className="absolute -top-1 -right-1 text-[10px] font-bold bg-primary text-primary-foreground rounded-full px-1.5 py-0.5">{pct}%</span>
      </button>

      {/* Painel expandido (com animação) */}
      <div
        className={`fixed bottom-40 right-5 z-[9998] w-72 rounded-2xl bg-[#0e1524] border border-border shadow-2xl p-4 origin-bottom-right transition-all duration-300 ${open ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto' : 'opacity-0 scale-90 translate-y-2 pointer-events-none'}`}
        onClick={(e) => e.stopPropagation()}
      >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 font-semibold text-sm">
              <Send className="w-4 h-4 text-primary" /> {isCampaign ? 'Campanha em massa' : (job?.kind === 'push' ? 'Notificações em massa' : 'Cobrança em massa')}
            </div>
            <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
          </div>

          {/* progresso X/Y */}
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold">{done}</span>
            <span className="text-muted-foreground">/ {total}</span>
            <span className="ml-auto text-sm font-semibold" style={{ color: statusColor }}>{pct}%</span>
          </div>

          {/* barra com animação */}
          <div className="h-2.5 rounded-full bg-muted/40 overflow-hidden mt-2 relative">
            <div className="h-full rounded-full transition-[width] duration-700 ease-out relative overflow-hidden"
                 style={{ width: `${pct}%`, background: statusColor }}>
              {running && <div className="absolute inset-0 bulk-shimmer" />}
            </div>
          </div>

          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
            <span className="text-green-500">✓ {done} enviados</span>
            {(job?.failed || 0) > 0 && <span className="text-red-400">✕ {job.failed} falhas</span>}
          </div>

          <div className="mt-3 flex items-center gap-2">
            {running ? (
              <>
                <span className="text-xs text-muted-foreground flex items-center gap-1.5 flex-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> {isCampaign ? 'Enviando campanha…' : 'Enviando…'} pode fechar a tela
                </span>
                <button onClick={isCampaign ? stopCampaign : cancel} className="text-xs font-medium text-red-500 hover:text-red-400">Parar envio</button>
              </>
            ) : (
              <>
                <span className="text-xs flex-1">
                  {job?.status === 'cancelled' ? 'Envio interrompido.' : 'Envio concluído! 🎉'}
                </span>
                <button onClick={dismiss} className="text-xs font-medium text-primary">Fechar</button>
              </>
            )}
          </div>
      </div>

      {/* fundo para fechar ao clicar fora */}
      {open && <div className="fixed inset-0 z-[9997]" onClick={() => setOpen(false)} />}

      <style>{`
        @keyframes bulk-shimmer-move { 0% { transform: translateX(-100%); } 100% { transform: translateX(200%); } }
        .bulk-shimmer { background: linear-gradient(90deg, transparent, rgba(255,255,255,.35), transparent); animation: bulk-shimmer-move 1.3s linear infinite; }
        @keyframes bulk-breathe { 0%,100% { box-shadow: 0 0 0 0 rgba(34,197,94,.4); } 50% { box-shadow: 0 0 0 8px rgba(34,197,94,0); } }
        .bulk-breathe { animation: bulk-breathe 2s ease-in-out infinite; }
      `}</style>
    </>
  );
}
